import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MAX_INSTRUMENT_SLOT_COUNT } from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { hasInstrumentClipboard } from "../lib/instrumentClipboard";
import { clampRowMarkerColor, ROW_MARKER_COLORS } from "../lib/trackColors";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore, type TrackLabelEditState } from "../store/uiStore";

/**
 * Floating color picker for a project-global track column tint.
 * Color applies immediately; dismiss with Escape or click outside.
 */
export function TrackLabelModal() {
  const edit = useStoreSelector(uiStore, s => s.trackLabelEdit);
  const trackCount = useStoreSelector(projectStore, s => s.instruments.length);
  const panelRef = useRef<HTMLDivElement>(null);
  const [color, setColor] = useState<number | null>(null);

  useEffect(() => {
    if (!edit) {
      return;
    }
    setColor(edit.color);
  }, [edit]);

  useEffect(() => {
    if (!edit) {
      return;
    }
    // Keep the hug-content panel on-screen after layout.
    const id = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) {
        return;
      }
      const rect = panel.getBoundingClientRect();
      const left = Math.min(window.innerWidth - rect.width - 8, Math.max(8, edit.anchor.left));
      const top = Math.min(window.innerHeight - rect.height - 8, Math.max(8, edit.anchor.bottom + 6));
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
    });
    return () => cancelAnimationFrame(id);
  }, [edit]);

  useEffect(() => {
    if (!edit) {
      return;
    }
    function close(): void {
      patchUi({ trackLabelEdit: null });
      getLabActions()?.focusTracker?.();
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onPointerDown(event: PointerEvent): void {
      const panel = panelRef.current;
      if (event.target instanceof Node && panel?.contains(event.target)) {
        return;
      }
      close();
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [edit]);

  if (!edit) {
    return null;
  }

  function close(): void {
    patchUi({ trackLabelEdit: null });
    getLabActions()?.focusTracker?.();
  }

  function applyColor(next: number | null): void {
    setColor(next);
    getLabActions()?.setTrackColor?.(edit.track, next);
  }

  const canDelete = trackCount > 1;
  const canPasteInstrument = hasInstrumentClipboard();
  const pos = positionFor(edit.anchor);

  return createPortal(
    <div
      ref={panelRef}
      className="track-label-modal fixed z-[55] flex w-max flex-col gap-[0.55rem] rounded-lg border border-lab-line bg-lab-panel px-[0.7rem] pt-[0.65rem] pb-[0.55rem] text-[12px] text-slate-300 shadow-[0_18px_40px_rgba(0,0,0,0.45)]"
      role="dialog"
      aria-label="Track color"
      style={{ left: pos.left, top: pos.top }}
    >
      <div className="text-[11px] font-semibold tracking-[0.04em] text-slate-100 uppercase">
        {`Track ${String(edit.track + 1).padStart(2, "0")}`}
      </div>
      <div className="flex flex-nowrap gap-[0.35rem]" role="listbox" aria-label="Track color">
        <button
          type="button"
          className={
            color == null
              ? "relative size-[1.45rem] shrink-0 rounded-full border-2 border-slate-50 bg-lab-bg p-0 shadow-[0_0_0_2px_rgba(15,18,22,0.9),inset_0_0_0_1px_rgba(255,255,255,0.12)]"
              : "relative size-[1.45rem] shrink-0 rounded-full border-2 border-lab-line bg-lab-bg p-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] hover:border-slate-400"
          }
          title="No color"
          aria-label="No color"
          aria-selected={color == null}
          onClick={() => applyColor(null)}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] leading-none text-slate-400"
          >
            ×
          </span>
        </button>
        {ROW_MARKER_COLORS.map((hex, i) => (
          <button
            key={hex}
            type="button"
            className={
              color === i
                ? "size-[1.45rem] shrink-0 rounded-full border-2 border-slate-50 p-0 shadow-[0_0_0_2px_rgba(15,18,22,0.9),inset_0_0_0_1px_rgba(0,0,0,0.25)]"
                : "size-[1.45rem] shrink-0 rounded-full border-2 border-transparent p-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.35)] hover:brightness-[1.08]"
            }
            style={{ background: hex }}
            title={`Color ${i + 1}`}
            aria-label={`Color ${i + 1}`}
            aria-selected={color === i}
            onClick={() => applyColor(clampRowMarkerColor(i))}
          />
        ))}
      </div>
      <div className="-mx-[0.2rem] mt-[0.1rem] border-t border-white/[0.08] pt-[0.35rem]">
        <button
          type="button"
          role="menuitem"
          className="block h-8 w-full rounded border-none bg-transparent px-[0.35rem] text-left text-[12px] text-slate-200 [font:inherit] hover:enabled:bg-white/8 hover:enabled:text-white disabled:cursor-not-allowed disabled:text-slate-500"
          title="Copy this track’s instrument, mix, and slot FX"
          onMouseDown={event => event.preventDefault()}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            const track = edit.track;
            close();
            getLabActions()?.copyInstrument(track);
          }}
        >
          Copy instrument
        </button>
        <button
          type="button"
          role="menuitem"
          className="block h-8 w-full rounded border-none bg-transparent px-[0.35rem] text-left text-[12px] text-slate-200 [font:inherit] hover:enabled:bg-white/8 hover:enabled:text-white disabled:cursor-not-allowed disabled:text-slate-500"
          title={
            canPasteInstrument
              ? "Paste copied instrument onto this track"
              : "Clipboard has no instrument"
          }
          disabled={!canPasteInstrument}
          onMouseDown={event => {
            if (canPasteInstrument) {
              event.preventDefault();
            }
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            if (!canPasteInstrument) {
              return;
            }
            const track = edit.track;
            close();
            getLabActions()?.pasteInstrument(track);
          }}
        >
          Paste instrument
        </button>
        <button
          type="button"
          role="menuitem"
          className="block h-8 w-full rounded border-none bg-transparent px-[0.35rem] text-left text-[12px] text-slate-200 [font:inherit] hover:enabled:bg-white/8 hover:enabled:text-white disabled:cursor-not-allowed disabled:text-slate-500"
          title={
            trackCount >= MAX_INSTRUMENT_SLOT_COUNT
              ? `Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT})`
              : "Duplicate this track to the right"
          }
          disabled={trackCount >= MAX_INSTRUMENT_SLOT_COUNT}
          onMouseDown={event => {
            if (trackCount < MAX_INSTRUMENT_SLOT_COUNT) {
              event.preventDefault();
            }
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            if (trackCount >= MAX_INSTRUMENT_SLOT_COUNT) {
              return;
            }
            const track = edit.track;
            close();
            getLabActions()?.duplicateTrack(track);
          }}
        >
          Duplicate
        </button>
        <button
          type="button"
          role="menuitem"
          className="block h-8 w-full rounded border-none bg-transparent px-[0.35rem] text-left text-[12px] text-rose-300/90 [font:inherit] hover:enabled:bg-white/8 hover:enabled:text-rose-200 disabled:cursor-not-allowed disabled:text-slate-500"
          title={canDelete ? "Delete this instrument track" : "Cannot delete the last track"}
          disabled={!canDelete}
          onMouseDown={event => {
            if (canDelete) {
              event.preventDefault();
            }
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            if (!canDelete) {
              return;
            }
            close();
            getLabActions()?.removeTrack(edit.track);
          }}
        >
          Delete track
        </button>
      </div>
    </div>,
    document.body,
  );
}

function positionFor(anchor: TrackLabelEditState["anchor"]): { left: number; top: number } {
  return {
    left: Math.max(8, anchor.left),
    top: Math.max(8, anchor.bottom + 6),
  };
}
